
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { IShortCommunity, IModule, ICommunitySubmodule } from '@/model/Community/communityInterfaces';
import { IAuthUserData } from '@/store/modules/authModule';
import { EPermissionActions } from '@/model/EPermissionActions';
import CreateButton from '@/components/shared/CreateButton.vue';
import CsvDownloader from '@/components/shared/CsvDownloader.vue';
import { communityService } from '@/services/communityService';
import { COMMUNITY_VERTICALS } from '@/model/Community/communityConstants';
import { moduleOptionsLabel } from '@/components/community/modules/moduleOptions';

const communityModule = namespace('communityModule');
const authModule = namespace('authModule');

interface IModulePill {
  id: number;
  name: string;
  icon: string | null;
  hasOptions: boolean;
  optionsLabel: string;
  picks: ICommunitySubmodule[];
}

@Component({
  components: { BoListPanel, PaginationBar, CreateButton, CsvDownloader },
})
export default class CommunityList extends Vue {
  @communityModule.Getter('communityList')
  private communityList!: IShortCommunity[];

  @communityModule.Getter('currentPage')
  private currentPage!: number;

  @communityModule.Getter('lastPage')
  private lastPage!: number;

  @communityModule.Getter('total')
  private total!: number;

  @communityModule.Getter('perPage')
  private perPage!: number;

  @communityModule.Action('fetchCommunities')
  private fetchCommunities!: (filters: any) => Promise<any>;

  @communityModule.Action('clearCommunities')
  private clearCommunities!: () => void;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @communityModule.Getter('availableModules')
  private availableModules!: IModule[];

  @communityModule.Action('fetchModules')
  private fetchModules!: () => Promise<any>;

  @communityModule.Getter('countries')
  private countries!: { id: number; name: string }[];

  @communityModule.Action('fetchCountries')
  private fetchCountries!: () => Promise<any>;

  private search: string = '';
  private filterStatus: string = '';
  private filterModuleIds: number[] = [];
  private filterExcludedModuleIds: number[] = [];
  private filterSubmoduleIds: number[] = [];
  private filterLocationCities: string[] = [];
  private filterCountryIds: number[] = [];
  private filterVerticals: number[] = [];
  private filtersExpanded: boolean = false;
  private selectedPerPage: number = 15;
  private searchTimer: any = null;
  private availableCities: string[] = [];
  private sortField: string = '';
  private sortDir: 'asc' | 'desc' = 'desc';
  private tableSortBy: string[] = [];
  private tableSortDesc: boolean[] = [];

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  get activeFilterCount(): number {
    return (this.filterStatus ? 1 : 0)
      + (this.filterModuleIds.length ? 1 : 0)
      + (this.filterExcludedModuleIds.length ? 1 : 0)
      + (this.filterSubmoduleIds.length ? 1 : 0)
      + (this.filterLocationCities.length ? 1 : 0)
      + (this.filterCountryIds.length ? 1 : 0)
      + (this.filterVerticals.length ? 1 : 0);
  }

  readonly perPageOptions: number[] = [15, 30, 50, 100];

  get canCreate(): boolean {
    return this.$canAction(EPermissionActions.ACTION_COMMUNITIES_CREATE);
  }

  get headers(): BetterDataTableHeader[] {
    const compactColumn = {
      width: '1%',
      class: 'text-no-wrap px-2',
      cellClass: 'text-no-wrap px-2',
    };

    return [
      { text: this.$t('common.id') as string, align: 'start', sortable: true, value: 'id', ...compactColumn },
      { text: this.$t('common.name') as string, align: 'start', sortable: true, value: 'name' },
      { text: this.$t('communities.fields.units') as string, align: 'start', sortable: true, value: 'units', ...compactColumn },
      { text: this.$t('lockers.locker.fields.vertical.title') as string, align: 'start', sortable: true, value: 'vertical', ...compactColumn },
      { text: this.$t('communities.fields.locationCity') as string, align: 'start', sortable: false, value: 'location_city', ...compactColumn },
    ];
  }

  /**
   * Header clicks cycle each column through: no sort -> DESC -> ASC -> no sort.
   * Vuetify's own cycle is no sort -> ASC -> DESC, so we only use its event to
   * know which column was clicked and keep the real state here.
   */
  onTableOptions(options: { sortBy: string[]; sortDesc: boolean[] }) {
    const by = options.sortBy[0] || '';
    const desc = !!options.sortDesc[0];
    if (by === this.sortField && (!by || desc === (this.sortDir === 'desc'))) return;

    const clicked = by || this.sortField;
    if (clicked !== this.sortField) {
      this.setSort(clicked, 'desc');
    } else if (this.sortDir === 'desc') {
      this.setSort(clicked, 'asc');
    } else {
      this.setSort('', 'desc');
    }
    this.reload();
  }

  private setSort(field: string, dir: 'asc' | 'desc') {
    this.sortField = field;
    this.sortDir = dir;
    // New arrays every time so v-data-table always re-syncs its internal state with ours
    this.tableSortBy = field ? [field] : [];
    this.tableSortDesc = field ? [dir === 'desc'] : [];
  }

  get statusFilterOptions() {
    return [
      { text: this.$t('communities.status.active'), value: 'active', color: 'green lighten-4', textColor: 'green darken-3' },
      { text: this.$t('communities.status.inactive'), value: 'inactive', color: 'grey lighten-3', textColor: 'grey darken-2' },
      { text: this.$t('communities.status.deleted'), value: 'deleted', color: 'red lighten-4', textColor: 'red darken-3' },
    ];
  }

  get verticalOptions() {
    return COMMUNITY_VERTICALS.map((id) => ({ text: this.verticalLabel(id), value: id }));
  }

  get modulesWithOptions(): IModule[] {
    return this.availableModules.filter((mod: IModule) => !!(mod.submodules && mod.submodules.length));
  }

  optionsLabel(mod: { slug: string }): string {
    return moduleOptionsLabel(mod.slug);
  }

  modulePills(item: IShortCommunity): IModulePill[] {
    const withOptions = this.modulesWithOptions.map((mod: IModule) => mod.id);
    return (item.modules || []).map((mod: IModule) => {
      const picks = (item.submodules || []).filter((sub: ICommunitySubmodule) => sub.module_id === mod.id);
      return {
        id: mod.id,
        name: mod.name,
        icon: mod.icon,
        hasOptions: picks.length > 0 || withOptions.includes(mod.id),
        optionsLabel: this.optionsLabel(mod),
        picks,
      };
    });
  }

  pickNames(pill: IModulePill, separator: string): string {
    return pill.picks.length
      ? pill.picks.map((pick: ICommunitySubmodule) => pick.name).join(separator)
      : this.$t('modules.noPick') as string;
  }

  verticalLabel(vertical: number): string {
    return this.$t('lockers.locker.fields.vertical.values.' + vertical).toString();
  }

  statusColor(status: string): string {
    if (status === 'active') return 'green darken-1';
    if (status === 'deleted') return 'red darken-1';
    return 'grey';
  }

  loadData() {
    this.fetchCommunities(this.buildFilters());
  }

  openCommunity(item: IShortCommunity) {
    this.$router.push({ name: 'community', params: { communityId: '' + item.id } });
  }

  communityRowClass(): string {
    return 'community-row';
  }

  mounted() {
    this.fetchModules();
    this.fetchCountries();
    this.loadCities();
    this.loadData();
  }

  private loadCities() {
    communityService.fetchCities().then((cities: string[]) => {
      this.availableCities = cities;
    });
  }

  private buildFilters(): any {
    return { page: 1, per_page: this.selectedPerPage, ...this.buildFilterParams() };
  }

  private buildFilterParams(): any {
    const filters: any = {};
    if (this.search) filters.search = this.search;
    if (this.filterStatus) filters.status = this.filterStatus;
    if (this.filterModuleIds.length) filters.module_id = this.filterModuleIds;
    if (this.filterExcludedModuleIds.length) filters.exclude_module_id = this.filterExcludedModuleIds;
    if (this.filterSubmoduleIds.length) filters.submodule_id = this.filterSubmoduleIds;
    if (this.filterLocationCities.length) filters.location_city = this.filterLocationCities;
    if (this.filterCountryIds.length) filters.location_country_id = this.filterCountryIds;
    if (this.filterVerticals.length) filters.vertical = this.filterVerticals;
    if (this.sortField) {
      filters.sort_by = this.sortField;
      filters.sort_dir = this.sortDir;
    }
    return filters;
  }

  onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.reload();
    }, 400);
  }

  onClearSearch() {
    this.search = '';
    this.reload();
  }

  onFilterChange() {
    this.reload();
  }

  // A module can't be included and excluded at once: picking it in one row drops it from the other
  onModuleFilterChange() {
    this.filterExcludedModuleIds = this.filterExcludedModuleIds.filter(id => !this.filterModuleIds.includes(id));
    this.reload();
  }

  onExcludedModuleFilterChange() {
    this.filterModuleIds = this.filterModuleIds.filter(id => !this.filterExcludedModuleIds.includes(id));
    this.filterSubmoduleIds = this.filterSubmoduleIds.filter(id => !this.excludedSubmoduleIds.includes(id));
    this.reload();
  }

  private get excludedSubmoduleIds(): number[] {
    return this.modulesWithOptions
      .filter((mod: IModule) => this.filterExcludedModuleIds.includes(mod.id))
      .reduce((ids: number[], mod: IModule) => ids.concat(this.submoduleIdsOf(mod)), []);
  }

  private submoduleIdsOf(mod: IModule): number[] {
    return (mod.submodules || []).map((sub) => sub.id);
  }

  submoduleFilterOf(mod: IModule): number[] {
    const ids = this.submoduleIdsOf(mod);
    return this.filterSubmoduleIds.filter(id => ids.includes(id));
  }

  onSubmoduleFilterChange(mod: IModule, ids: number[]) {
    const own = this.submoduleIdsOf(mod);
    this.filterSubmoduleIds = this.filterSubmoduleIds.filter(id => !own.includes(id)).concat(ids || []);
    if (ids && ids.length) {
      this.filterExcludedModuleIds = this.filterExcludedModuleIds.filter(id => id !== mod.id);
    }
    this.reload();
  }

  private reload() {
    this.clearCommunities();
    this.fetchCommunities(this.buildFilters());
  }

  onPageChange(page: number) {
    this.clearCommunities();
    const filters: any = this.buildFilters();
    filters.page = page;
    this.fetchCommunities(filters);
  }

  onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.reload();
  }

  get csvHeaders(): string[] {
    return ['id', 'name', 'slug', 'status', 'active', 'modules', 'invitation_code',
      'vertical', 'languages', 'currency', 'location_name', 'location_line_1', 'location_line_2', 'location_city', 'location_postcode', 'country_name',
      'coordinates_lat', 'coordinates_long', 'native_app_enabled', 'rentback_enabled',
      'rentback_cap', 'locker_count', 'contract_start_date', 'contract_end_date',
      'maintenance_fee', 'saas_fee', 'hardware_fee', 'hardware_sale_price', 'units',
      'created_at', 'updated_at', 'submodules'];
  }

  get csvFileName(): string {
    return 'communities_export.csv';
  }

  csvMapRow(item: any): Array<string | number | null | undefined> {
    return [
      item.id,
      item.name || '',
      item.slug || '',
      item.status || '',
      item.active || '0',
      item.modules || '',
      item.invitation_code || '',
      item.vertical != null ? this.verticalLabel(item.vertical) : '',
      item.languages || '',
      item.currency || '',
      item.location_name || '',
      item.location_line_1 || '',
      item.location_line_2 || '',
      item.location_city || '',
      item.location_postcode || '',
      item.country_name || '',
      item.coordinates_lat != null ? item.coordinates_lat : '',
      item.coordinates_long != null ? item.coordinates_long : '',
      item.native_app_enabled || '0',
      item.rentback_enabled || '0',
      item.rentback_cap != null ? item.rentback_cap : '',
      item.locker_count != null ? item.locker_count : 0,
      item.contract_start_date || '',
      item.contract_end_date || '',
      item.maintenance_fee != null ? item.maintenance_fee : '',
      item.saas_fee != null ? item.saas_fee : '',
      item.hardware_fee != null ? item.hardware_fee : '',
      item.hardware_sale_price != null ? item.hardware_sale_price : '',
      item.units != null ? item.units : '',
      item.created_at || '',
      item.updated_at || '',
      item.submodules || '',
    ];
  }

  fetchCommunitiesCsv(): Promise<any[]> {
    return communityService.exportCommunities(this.buildFilterParams());
  }
}
